import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { usePostHog } from "posthog-js/react";

import { ErrorTypography } from "components/ErrorTypography";
import { ClientAppEvent } from "constants/index";
import { useDisclosure, useWriterComponentState } from "hooks";

interface TfcApproveButtonProps {
  tfcId: number;
  shipmentId: number;
  carrierCompanyName: string;
  shipperCompanyName: string;
  totalFormatted: string;
  onSuccess: () => void;
  undoReject?: boolean;
}

export function TfcApproveButton(props: TfcApproveButtonProps) {
  const {
    tfcId,
    shipmentId,
    carrierCompanyName,
    shipperCompanyName,
    totalFormatted,
    onSuccess,
    undoReject = false,
  } = props;
  const { isOpen, onOpen, onClose } = useDisclosure(false);
  const posthog = usePostHog();

  const endpoint = undoReject ? "undo-and-accept" : "approve";
  const event = undoReject
    ? ClientAppEvent.TFC_UNDO_AND_ACCEPTED
    : ClientAppEvent.TFC_APPROVED;

  const { loading, write, error } = useWriterComponentState({
    errorMessage: "Failed to confirm freight cost. Please try again.",
    writeConfig: {
      method: "post",
      url: `/tfc/${tfcId}/${endpoint}`,
      body: {},
    },
  });

  const handleConfirm = () => {
    onClose();
    write(() => {
      posthog?.capture(event, {
        shipment_id: shipmentId,
        shipper_company_name: shipperCompanyName,
        carrier_company_name: carrierCompanyName,
      });
      onSuccess();
    });
  };

  const buttonLabel = undoReject ? "CANCEL CHANGE REQUEST & ACCEPT" : "CONFIRM";

  const dialogTitle = undoReject
    ? "Cancel Change Request"
    : "Confirm Freight Cost";

  const dialogBody = undoReject ? (
    "This will cancel your change request and accept the carrier's submitted freight costs as-is. Continue?"
  ) : (
    <>
      You are confirming that a total freight cost of{" "}
      <strong>{totalFormatted}</strong> is correct for this shipment. Continue?
    </>
  );

  return (
    <>
      <Button
        variant={undoReject ? "outlined" : "contained"}
        disabled={loading}
        onClick={onOpen}
        sx={
          undoReject
            ? {
                px: 3,
                color: "#D32F2F",
                borderColor: "#D32F2F",
                "&:hover": {
                  borderColor: "#C62828",
                  backgroundColor: "rgba(211, 47, 47, 0.04)",
                },
              }
            : {
                px: 3,
                backgroundColor: "#2E7D32",
                "&:hover": { backgroundColor: "#1B5E20" },
              }
        }
      >
        {buttonLabel}
      </Button>

      <ErrorTypography error={error} />

      <Dialog open={isOpen} onClose={onClose} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 500, fontSize: "20px" }}>
          {dialogTitle}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "14px" }}>{dialogBody}</Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={onClose} sx={{ color: "#6B7280" }}>
            CANCEL
          </Button>
          <Button variant="contained" onClick={handleConfirm} sx={{ px: 3 }}>
            CONFIRM
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
